import { Mutation, Query } from "@apollo/client/react/components";
import { Button, Input, LoadingIndicator } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import Link from "next/link";
import React, { ChangeEvent, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Toggle } from "react-powerplug";
import { useDebounce } from "react-use";
import RemoveInvitee from "~/lib/mutations/RemoveInvitee";
import {
  IRemoveInviteeMutation,
  IRemoveInviteeMutationVariables,
} from "~/lib/mutations/__generated__/RemoveInvitee.generated";
import GroupMembersQuery from "~/lib/queries/GroupMembersQuery";
import {
  IGroupMembersQuery,
  IGroupMembersQueryVariables,
} from "~/lib/queries/__generated__/GroupMembersQuery.generated";
import { IGroupQuery } from "~/lib/queries/__generated__/GroupQuery.generated";
import mediaQueries from "~/lib/utils/mediaQueries";
import IntlRelativeTime from "~/src/shared/components/IntlRelativeTime";
import { IUser } from "~/types/types";
import { useSelfie } from "~/components/auth/Auth";
import InviteModal from "~/components/invite/InviteModal";
import { ShowMore } from "~/components/profile/ShowMore";
import { Margin } from "~/components/spacings";
import Dotdotdot from "~/components/text/Dotdotdot";
import NoResultsFound from "~/components/group/NoResultsFound";

interface IMembers {
  group: NonNullable<IGroupQuery["groupBySlug"]>;
}

export default function Members({ group }: IMembers) {
  const user = useSelfie();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const intl = useIntl();

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    300,
    [search],
  );

  return (
    <Query<IGroupMembersQuery, IGroupMembersQueryVariables>
      query={GroupMembersQuery}
      notifyOnNetworkStatusChange={true}
      variables={{
        slug: group.slug,
        cursor: null,
        search: debouncedSearch,
      }}
    >
      {({ data, loading, fetchMore }) => {
        const members =
          data && data.groupBySlug && data.groupBySlug.members && data.groupBySlug.members
            ? data.groupBySlug.members
            : null;

        return (
          <div
            data-intercom-target="Group Members"
            css={{
              borderRadius: 5,
              border: `1px solid ${palette.lightestGray}`,
              background: "#fff",
            }}
          >
            <div
              css={{
                padding: 16,
              }}
            >
              <Toggle>
                {({ on: isOpen, toggle }) => (
                  <Margin
                    mb={16}
                    css={{
                      display: "flex",
                    }}
                  >
                    <Input
                      placeholder={intl.formatMessage({
                        defaultMessage: "Search for members",
                        id: "O4epE5",
                      })}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                    />
                    {group.viewerIsMember && (
                      <Button
                        theme="link"
                        color={palette.innovatorBlue}
                        textColor={palette.innovatorBlue}
                        onClick={toggle}
                        css={{
                          color: palette.innovatorBlue,
                          marginLeft: 16,
                          textTransform: "uppercase",
                        }}
                      >
                        Invite
                      </Button>
                    )}

                    <InviteModal
                      title={intl.formatMessage({ defaultMessage: "Invite Members", id: "xNdoS4" })}
                      subtitle={
                        <span>
                          {intl.formatMessage({
                            defaultMessage: "They’ll be granted access to edit this Group.",
                            id: "PUGBo2",
                          })}
                        </span>
                      }
                      targetType="GROUP"
                      targetId={group._id}
                      isOpen={isOpen}
                      onRequestClose={toggle}
                      inviteByEmail={false}
                    />
                  </Margin>
                )}
              </Toggle>

              <div
                css={mediaQueries({
                  display: "grid",
                  gridTemplateColumns: ["1fr", "1fr", "1fr 1fr 1fr"],
                  gridColumnGap: 32,
                  gridRowGap: 32,
                })}
              >
                {members && members.items && members.items.length === 0 && debouncedSearch.length > 0 && (
                  <NoResultsFound />
                )}

                {members &&
                  members.items &&
                  members.items.map((member) => (
                    <div
                      key={member._id}
                      css={{
                        display: "flex",
                        overflow: "hidden",
                      }}
                    >
                      {member.slug ? (
                        <Link
                          passHref={true}
                          key={member._id}
                          href="/[userSlug]/[[...tab]]"
                          as={`/@${member.slug}`}
                          legacyBehavior={true}
                        >
                          <a>
                            <ProfilePicture
                              image={member.avatar}
                              name={member.fullName || member.firstName}
                              size={60}
                              css={{
                                borderRadius: 6,
                                "& > img": {
                                  borderRadius: 6,
                                  padding: 0,
                                },
                                padding: 0,
                              }}
                            />
                          </a>
                        </Link>
                      ) : (
                        <ProfilePicture
                          image={member.avatar}
                          name={member.firstName}
                          size={60}
                          css={{
                            borderRadius: 6,
                            "& > img": {
                              borderRadius: 6,
                              padding: 0,
                            },
                            padding: 0,
                          }}
                        />
                      )}

                      <div
                        css={{
                          display: "flex",
                          flexDirection: "column",
                          marginLeft: 12,
                        }}
                      >
                        <UserLink member={member} />

                        {user &&
                          (user._id === member._id || group.viewerCanRemoveMembers) &&
                          group.viewerCanHeadContent && (
                            <Mutation<IRemoveInviteeMutation, IRemoveInviteeMutationVariables>
                              mutation={RemoveInvitee}
                              variables={{
                                targetId: group._id,
                                targetType: "GROUP",
                                inviteeId: member._id,
                              }}
                            >
                              {(removeInvitee) => (
                                <span
                                  role="button"
                                  css={{
                                    display: "block",
                                    color: palette.red,
                                    fontSize: 14,
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    if (window.confirm("Are you sure?")) {
                                      removeInvitee().then(() => {
                                        if (user._id === member._id) {
                                          window.location.href = "/";
                                        }
                                      });
                                    }
                                  }}
                                >
                                  {user._id === member._id ? (
                                    <FormattedMessage defaultMessage="Remove yourself" id="2x6kwI" />
                                  ) : (
                                    <FormattedMessage defaultMessage="Remove from Group" id="ehGDpT" />
                                  )}
                                </span>
                              )}
                            </Mutation>
                          )}
                      </div>
                    </div>
                  ))}
              </div>

              {loading && (
                <div
                  css={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <LoadingIndicator />
                </div>
              )}
            </div>

            {members && members.pageInfo && members.pageInfo.hasNextPage && (
              <ShowMore
                onClick={async () => {
                  fetchMore({
                    variables: {
                      slug: group.slug,
                      search: debouncedSearch,
                      cursor: members.pageInfo ? members.pageInfo.endCursor : null,
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                      if (!fetchMoreResult) {
                        return previousResult;
                      }

                      return {
                        __typename: "Query",
                        groupBySlug: {
                          ...fetchMoreResult.groupBySlug!,
                          members: {
                            ...fetchMoreResult.groupBySlug!.members,
                            items: [
                              ...previousResult.groupBySlug!.members!.items!,
                              ...fetchMoreResult.groupBySlug!.members!.items!,
                            ],
                          },
                        },
                      };
                    },
                  });
                }}
              />
            )}
          </div>
        );
      }}
    </Query>
  );
}

function UserLink({
  member,
}: {
  member: Pick<IUser, "_id" | "slug" | "firstName" | "fullName" | "jobTitle" | "createdAt">;
}) {
  if (member.slug) {
    return (
      <Link
        passHref={true}
        key={member._id}
        href="/[userSlug]/[[...tab]]"
        as={`/@${member.slug}`}
        legacyBehavior={true}
      >
        <a
          css={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: palette.sealBlue,
          }}
        >
          <p
            css={{
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              margin: 0,
              fontSize: 18,
              fontWeight: "bold",
              color: palette.primary,
            }}
          >
            {member.fullName || member.firstName}
          </p>
          {member.jobTitle && member.jobTitle.length > 1 ? (
            <p
              css={{
                fontSize: 13,
                color: palette.sealBlue,
                lineHeight: "17px",
              }}
            >
              <Dotdotdot clamp={1}>{member.jobTitle}</Dotdotdot>
            </p>
          ) : (
            <p
              css={{
                fontSize: 13,
                color: palette.sealBlue,
                lineHeight: "17px",
              }}
            >
              <FormattedMessage defaultMessage="Joined" id="l8wanU" />
              <IntlRelativeTime value={member.createdAt} />
            </p>
          )}
        </a>
      </Link>
    );
  }

  return (
    <div
      css={{
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      <p
        css={{
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          margin: 0,
          fontSize: 18,
          fontWeight: "bold",
          color: palette.primary,
        }}
      >
        {member.firstName}
      </p>
      {member.jobTitle && member.jobTitle.length > 1 ? (
        <p
          css={{
            fontSize: 13,
            color: palette.sealBlue,
            lineHeight: "17px",
          }}
        >
          <Dotdotdot clamp={1}>{member.jobTitle}</Dotdotdot>
        </p>
      ) : (
        <p
          css={{
            fontSize: 13,
            color: palette.sealBlue,
            lineHeight: "17px",
          }}
        >
          <FormattedMessage defaultMessage="Joined" id="l8wanU" />
          <IntlRelativeTime value={member.createdAt} />
        </p>
      )}
    </div>
  );
}
